import React from "react";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import {
  IconMapPin,
  IconCalendar,
  IconBuilding,
  IconPhone,
  IconBrandWhatsapp,
  IconCircleCheck,
  IconArrowRight,
} from "@tabler/icons-react";

interface RealtorProfileProps {
  full_name: string;
  joinedYears: string;
  listedProperties: number;
  location: string;
  whatsapp: string;
  isVerified?: boolean;
  realtorId?: string;
  onViewProperties?: () => void;
  profile_image_url?: string | null;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const RealtorProfile = ({
  full_name,
  joinedYears,
  listedProperties,
  location,
  whatsapp,
  isVerified = false,
  realtorId,
  onViewProperties,
  profile_image_url,
}: RealtorProfileProps) => {
  const router = useRouter();

  const handleViewProperties = () => {
    if (onViewProperties) {
      onViewProperties();
    } else if (realtorId) {
      router.push(`/realtors/${realtorId}`);
    }
  };
  const capitalizeFirst = (text: string): string =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

  return (
    <div className="group bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-[#2D96A7]/30 transition-all duration-300 overflow-hidden">
      {/* Top accent bar */}
      <div className="h-24 bg-gradient-to-r from-[#2D96A7] to-[#2D96A7]/60" />

      <div className="px-5 pb-5">
        {/* Header: Profile Image + Verified badge */}
        <div className="flex justify-between items-start -mt-12 mb-4">
          <div className="w-24 h-24 rounded-xl overflow-hidden border-4 border-white bg-white shadow-md flex items-center justify-center">
            {profile_image_url ? (
              <img
                src={profile_image_url}
                alt={full_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-2xl font-bold text-[#2D96A7]">
                {getInitials(full_name)}
              </span>
            )}
          </div>

          {isVerified && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#2D96A7]/10 text-[#2D96A7] border border-[#2D96A7]/30 mt-14">
              <IconCircleCheck className="w-3 h-3" />
              Verified
            </span>
          )}
        </div>

        {/* Name */}
        <h3 className="text-lg font-bold text-gray-900 mb-4 truncate">
          {full_name}
        </h3>

        {/* Details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3 text-sm">
            <IconCalendar className="w-4 h-4 text-[#2D96A7] flex-shrink-0" />
            <span className="text-gray-500">Joined</span>
            <span className="ml-auto text-gray-800 font-medium">
              {capitalizeFirst(joinedYears)}
            </span>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <IconBuilding className="w-4 h-4 text-[#2D96A7] flex-shrink-0" />
            <span className="text-gray-500">Listed properties</span>
            <span className="ml-auto text-gray-800 font-medium">
              {listedProperties}
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm min-w-0">
            <IconMapPin className="w-4 h-4 text-[#2D96A7] flex-shrink-0" />
            <span className="text-gray-500 flex-shrink-0">Location</span>
            <span
              className="ml-auto text-gray-800 font-medium truncate text-right pl-2"
              title={location}
            >
              {location}
            </span>
          </div>

          {/* WhatsApp - always visible */}
          {/* <div className="flex items-center gap-3 text-sm pt-2 border-t border-gray-100">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <IconBrandWhatsapp className="w-4 h-4 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-gray-400 uppercase tracking-wider block">Contact</span>
              <span className="text-sm font-medium text-gray-800 truncate block">{whatsapp}</span>
            </div>
          </div> */}
        </div>

        {/* View Properties Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewProperties();
          }}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#2D96A7] hover:bg-[#2D96A7]/90 text-white text-sm font-semibold rounded-lg transition-all duration-200"
        >
          View
          <IconArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default RealtorProfile;
